import React from 'react';
import {
  BathFixtureShell,
  Rect,
  Line,
  Curve,
  Circle,
} from './BathFixtureFrame';
import { WallHungSinkSchematic } from './WallHungSinkSchematic';
import { BATHROOM_SINK_DRAIN_RADIUS_RATIO } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Bathroom sink variants — dispatched on `obj.sinkType` (the canonical
 * field for `type: 'sink'`, see `shared-lib/sink-types.js`). Values:
 *   - undermount (default): the bowl hangs under the slab; the object
 *     box top IS the slab underside (calculateSinkSeatY.js).
 *   - dropin: rim rests on the slab (box top = slab top); a thin rim
 *     band shows above the bowl.
 *   - vessel: the bowl stands ON the slab (box bottom = slab top).
 *   - pedestal: full-height silhouette — bowl on top, pedestal narrow
 *     pillar to floor.
 *   - wall-mount: the procedural wall-hung basin, delegated to
 *     WallHungSinkSchematic — the twin of the 3D renderer that
 *     resolveProceduralType routes this sinkType to.
 *
 * The three bowl variants share one 3D model, `bathroomSinkRenderer`: a
 * half-ellipsoid filling the whole box (rim at the top, deepest point at
 * the bottom — see the BATHROOM SINK section of proceduralSpecs.js). Its
 * section is a half-ellipse in EITHER direction, so the front (width) and
 * side (depth) views draw the same `BowlSection` at their own span.
 *
 * `pedestal` and `wall-mount` are standalone sinks (no countertop
 * parent) — see the COUNTERTOP_MOUNTED_SINK_TYPES carve-out in
 * sink-types.js.
 */

const VALID_TYPES = new Set([
  'undermount',
  'vessel',
  'dropin',
  'drop-in',
  'pedestal',
  'wall-mount',
  'wallmount',
]);

export function resolveSinkVariant(obj = {}) {
  const t = obj.sinkType || obj.subtype;
  if (t === 'drop-in' || t === 'dropin') return 'dropin';
  if (t === 'wallmount' || t === 'wall-mount') return 'wall-mount';
  if (VALID_TYPES.has(t)) return t;
  return 'undermount';
}

/** Drain disc radius, the renderer's own rule: a share of min(width, depth). */
function drainRadius(width, depth) {
  return (
    Math.min(...[width, depth].filter(Number.isFinite)) *
    BATHROOM_SINK_DRAIN_RADIUS_RATIO
  );
}

export function SinkSchematic({
  widthMeters,
  heightMeters,
  depthMeters,
  obj = {},
  strokeColor,
  strokeWidth,
  view = 'front',
}) {
  const variant = resolveSinkVariant(obj);
  if (variant === 'wall-mount') {
    return (
      <WallHungSinkSchematic
        widthMeters={widthMeters}
        heightMeters={heightMeters}
        depthMeters={depthMeters}
        view={view}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }
  const stroke = { strokeColor, strokeWidth };
  const drainR = drainRadius(widthMeters, depthMeters);
  if (view === 'left' || view === 'right') {
    return (
      <SideView
        view={view}
        variant={variant}
        depth={depthMeters}
        height={heightMeters}
        drainR={drainR}
        stroke={stroke}
      />
    );
  }
  return (
    <FrontView
      variant={variant}
      width={widthMeters}
      height={heightMeters}
      drainR={drainR}
      stroke={stroke}
    />
  );
}

SinkSchematic.supportsSideViews = true;

function FrontView({ variant, width, height, drainR, stroke }) {
  return (
    <BathFixtureShell width={width} height={height}>
      <Variant
        variant={variant}
        span={width}
        height={height}
        drainR={drainR}
        stroke={stroke}
      />
    </BathFixtureShell>
  );
}

/**
 * Side elevation. Symmetric L vs R (a bathroom sink looks the same
 * from either side); the wall is at x = 0 in the un-mirrored frame.
 */
function SideView({ view, variant, depth, height, drainR, stroke }) {
  return (
    <BathFixtureShell width={depth} height={height}>
      <g
        transform={
          view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
        }
      >
        <Variant
          variant={variant}
          span={depth}
          height={height}
          drainR={drainR}
          stroke={stroke}
        />
      </g>
    </BathFixtureShell>
  );
}

function Variant({ variant, span, height, drainR, stroke }) {
  const props = { span, height, drainR, stroke };
  if (variant === 'vessel') return <Vessel {...props} />;
  if (variant === 'pedestal') return <Pedestal {...props} />;
  if (variant === 'dropin') return <DropIn {...props} />;
  return <Undermount {...props} />;
}

/**
 * Bowl section: rim line across the top, the half-ellipse hanging from
 * it to the floor (rx = span/2, ry = the bowl height — the renderer's
 * scaled hemisphere cut through either axis), and the drain disc at
 * the deepest point.
 */
function BowlSection({ span, y0, y1, drainR, stroke }) {
  const rx = span / 2;
  const ry = y1 - y0;
  return (
    <g>
      <Line x1={0} y1={y0} x2={span} y2={y0} {...stroke} />
      <Curve d={`M 0 ${y0} A ${rx} ${ry} 0 0 0 ${span} ${y0}`} {...stroke} />
      <Circle cx={span / 2} cy={y1 - drainR} r={drainR} {...stroke} />
    </g>
  );
}

/** Undermount: the whole box is the bowl, rim at the slab underside. */
function Undermount({ span, height, drainR, stroke }) {
  return (
    <BowlSection
      span={span}
      y0={0}
      y1={height}
      drainR={drainR}
      stroke={stroke}
    />
  );
}

/**
 * Drop-in: the rim rests on the slab, so its lip shows as a slim band
 * above the counter with the bowl hanging from it. The band is the
 * one 2D-only feature here — the 3D bowl has no separate lip.
 */
function DropIn({ span, height, drainR, stroke }) {
  const rimH = Math.min(height * 0.12, 0.03);
  return (
    <g>
      <Rect x={0} y={0} width={span} height={rimH} {...stroke} />
      <BowlSection
        span={span}
        y0={rimH}
        y1={height}
        drainR={drainR}
        stroke={stroke}
      />
    </g>
  );
}

/** Vessel: the bowl stands on the slab, which is the box's bottom edge. */
function Vessel({ span, height, drainR, stroke }) {
  return (
    <g>
      <BowlSection
        span={span}
        y0={0}
        y1={height}
        drainR={drainR}
        stroke={stroke}
      />
      <Line x1={0} y1={height} x2={span} y2={height} {...stroke} />
    </g>
  );
}

/**
 * Pedestal: bowl on top, narrow column below to the floor. Bowl is a
 * rectangle (~38% of height); column is a centered narrow rectangle.
 * Same silhouette on the width and depth axes.
 */
function Pedestal({ span, height, stroke }) {
  const bowlH = height * 0.38;
  const colW = span * 0.32;
  const colX = (span - colW) / 2;
  return (
    <g>
      <Rect x={0} y={0} width={span} height={bowlH} {...stroke} />
      <Rect
        x={colX}
        y={bowlH}
        width={colW}
        height={height - bowlH}
        {...stroke}
      />
    </g>
  );
}
